import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';

import { accent, font, fontSize, neutral, radius, text as textToken } from 'styles/revampTokens';

import { formatDeliveryScheduleSummary } from './deliverySchedulePresets';

import type { DeliveryRecord } from './maestroDeliveryClient';

function scheduleSummary(row: DeliveryRecord): string {
  const emailCount = row.recipients?.filter((r) => r.type === 'email' && r.address).length ?? 0;
  return `${formatDeliveryScheduleSummary(row.schedule)} · ${emailCount} recipient${
    emailCount === 1 ? '' : 's'
  }`;
}

export interface DeliveryScheduleListProperties {
  deliveries: DeliveryRecord[];
  loading?: boolean;
  selectedId?: string | null;
  onEdit: (deliveryId: string) => void;
  onCreateNew: () => void;
  /** When false, parent renders create (e.g. export modal footer). */
  showHeaderCreate?: boolean;
}

/**
 * Lists saved maestro deliveries for the current dashboard pack.
 */
const DeliveryScheduleList = ({
  deliveries,
  loading = false,
  selectedId,
  onEdit,
  onCreateNew,
  showHeaderCreate = true,
}: DeliveryScheduleListProperties) => {
  if (loading) {
    return (
      <Flex align='center' gap={2} py={4} color={textToken.secondary} fontSize={fontSize.body}>
        <Spinner size='sm' color={accent.solid} />
        <Text>Loading schedules…</Text>
      </Flex>
    );
  }

  return (
    <Flex direction='column' gap={3}>
      <Flex justify='space-between' align='center' gap={2} flexWrap='wrap'>
        <Text fontSize={fontSize.label} fontWeight={600} color={textToken.secondary}>
          Saved schedules
        </Text>
        {showHeaderCreate && (
          <Button
            variant='outline'
            borderColor={neutral.border}
            borderRadius={radius.control}
            fontFamily={font.body}
            fontSize={fontSize.body}
            h='auto'
            minH='40px'
            flexShrink={0}
            padding='10px 22px'
            color={textToken.primary}
            bg={neutral.surface}
            _hover={{ bg: neutral.surfaceHover }}
            onClick={onCreateNew}
          >
            New schedule
          </Button>
        )}
      </Flex>

      {deliveries.length === 0 ? (
        <Text fontSize={fontSize.body} color={textToken.muted}>
          No automatic distributions yet. Create one to email this pack on a schedule.
        </Text>
      ) : (
        <Flex direction='column' gap={2}>
          {deliveries.map((row) => {
            const selected = selectedId === row.id;
            return (
              <Flex
                key={row.id}
                align='center'
                justify='space-between'
                gap={3}
                p={3}
                borderRadius={radius.control}
                border='1px solid'
                borderColor={selected ? accent.border : neutral.border}
                bg={selected ? accent.subtle : neutral.surface}
              >
                <Box minW={0} flex={1}>
                  <Text
                    fontFamily={font.body}
                    fontSize={fontSize.body}
                    fontWeight={600}
                    color={textToken.primary}
                    noOfLines={1}
                  >
                    {row.name || 'Untitled schedule'}
                  </Text>
                  <Text fontSize={fontSize.label} color={textToken.secondary} mt={0.5}>
                    {row.status} · {scheduleSummary(row)}
                  </Text>
                </Box>
                <Button
                  variant='outline'
                  borderColor={neutral.border}
                  borderRadius={radius.control}
                  fontFamily={font.body}
                  fontSize={fontSize.body}
                  h='auto'
                  minH='40px'
                  flexShrink={0}
                  padding='10px 22px'
                  color={textToken.primary}
                  bg={neutral.surface}
                  _hover={{ bg: neutral.surfaceHover }}
                  onClick={() => onEdit(row.id)}
                >
                  Edit
                </Button>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

export default DeliveryScheduleList;
